// src/cashup/ActiveStaffTab.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { collection, doc, getDoc, getDocs, query as q, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useOrgId } from '../utils/tenant';

// --- react-select dark theme like other tabs ---
const selectTheme = (t) => ({
  ...t,
  colors: {
    ...t.colors,
    primary25: '#334155',
    primary: '#1e293b',
    neutral0: '#1e293b',
    neutral80: '#ffffff',
  },
});
const selectStyles = {
  control: (base) => ({ ...base, backgroundColor: '#1e293b', borderColor: '#334155', color: '#fff' }),
  menu: (base) => ({ ...base, backgroundColor: '#1e293b' }),
  option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#334155' : '#1e293b', color: '#fff' }),
};

// --- helpers ---
const toSec = (v) =>
  v?.seconds ??
  (v?.toDate ? Math.floor(v.toDate().getTime() / 1000) : typeof v === 'number' ? v : null);

const DEFAULT_ABANDONED_SHIFT_HOURS = 18;
const clampAbandonedShiftHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_ABANDONED_SHIFT_HOURS;
  return Math.min(48, Math.max(1, Math.round(n)));
};

function durHM(fromMs, toMs) {
  const ms = Math.max(0, toMs - fromMs);
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}

export default function ActiveStaffTab({ siteScope = { allSites: true, siteIds: [] } }) {
  const orgId = useOrgId();

  const [stores, setStores] = useState([{ value: 'ALL', label: 'All stores' }]);
  const [store, setStore] = useState({ value: 'ALL', label: 'All stores' });

  const [active, setActive] = useState([]); // base, org-wide (today) active rows
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [archivedStaffNames, setArchivedStaffNames] = useState(new Set());
  const [abandonedShiftHours, setAbandonedShiftHours] = useState(DEFAULT_ABANDONED_SHIFT_HOURS);

  // tolerant id<->name maps (legacy shifts may store name instead of id)
  const storeIdToNameRef = useRef({});
  const storeNameToIdRef = useRef({});


  useEffect(() => {
    if (!orgId) return;

    // C8.9 closeout: archived staff membership is admin/configuration state.
    // Read it once when Active Staff opens instead of holding the whole users
    // collection on a realtime listener.
    getDocs(collection(db, 'orgs', orgId, 'cashup_users'))
      .then((snap) => {
        const names = new Set(
          snap.docs
            .map((d) => d.data() || {})
            .filter((u) => u.isArchived === true || u.archived === true)
            .map((u) => String(u.name || '').trim().toLowerCase())
            .filter(Boolean)
        );
        setArchivedStaffNames(names);
      })
      .catch(() => setArchivedStaffNames(new Set()));
  }, [orgId]);

  // load org stores (A–Z, dedupe by name)
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'orgs', orgId, 'cashup_stores'));
        const allowedSites = new Set(siteScope.siteIds || []);
        const raw = snap.docs.map((d) => ({ id: d.id, name: String(d.data()?.name || d.id).trim() })).filter((s) => siteScope.allSites || allowedSites.has(s.id));
        const seen = new Set();
        const uniq = [];
        for (const s of raw.sort((a, b) => a.name.localeCompare(b.name))) {
          const k = s.name.toLowerCase();
          if (seen.has(k)) continue;
          seen.add(k);
          uniq.push(s);
        }
        const idToName = {};
        const nameToId = {};
        uniq.forEach((s) => {
          idToName[s.id] = s.name;
          nameToId[s.name] = s.id;
        });
        storeIdToNameRef.current = idToName;
        storeNameToIdRef.current = nameToId;

        setStores([{ value: 'ALL', label: 'All stores' }, ...uniq.map((s) => ({ value: s.id, label: s.name }))]);

        if (store.value !== 'ALL' && !uniq.find((x) => x.id === store.value)) {
          setStore({ value: 'ALL', label: 'All stores' });
        }
      } catch {
        // ignore; still show legacy data below
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, siteScope.allSites, siteScope.siteIds]);


  useEffect(() => {
    if (!orgId) return;
    const loadThreshold = async () => {
      try {
        const snap = await getDoc(doc(db, 'orgs', orgId, 'settings', 'labor'));
        const data = snap.exists() ? snap.data() || {} : {};
        setAbandonedShiftHours(clampAbandonedShiftHours(data.abandonedShiftHours));
      } catch {
        setAbandonedShiftHours(DEFAULT_ABANDONED_SHIFT_HOURS);
      }
    };
    loadThreshold();
  }, [orgId]);

  // nested reader (per store) — only picks open shifts
  const readNestedPerStore = useCallback(
    async (storeId) => {
      try {
        const col = collection(db, 'orgs', orgId, 'stores', storeId, 'shifts');
        const snap = await getDocs(col);
        return snap.docs
          .map((d) => {
            const data = d.data() || {};
            const ci =
              toSec(data.clockIn) ??
              toSec(data.clockInAt) ??
              toSec(data.in) ??
              toSec(data.inAt) ??
              null;

            const co =
              toSec(data.clockOut) ??
              toSec(data.clockOutAt) ??
              toSec(data.out) ??
              toSec(data.outAt) ??
              null;
          if (ci == null) return null;
          // include both active and clocked-out shifts so we can correctly dedupe (latest wins) (no clockOut)
            return {
              id: d.id,
              name: data.name || data.staffName || data.employeeName || '',
              role: data.role || data.roleName || '',
              store: storeId, // id
              clockInSec: ci,
            clockOutSec: co,
              sourceKey: `orgs/${orgId}/stores/${storeId}/shifts/${d.id}`,
            };
          })
          .filter(Boolean);
      } catch {
        return [];
      }
    },
    [orgId]
  );

  // legacy reader (org-scoped) — only picks open shifts
  const readLegacyActive = useCallback(async () => {
    try {
      const snap = await getDocs(q(collection(db, 'cashup_shifts'), where('orgId', '==', orgId)));
      return snap.docs
        .map((d) => {
          const data = d.data() || {};
          const ci =
            toSec(data.clockIn) ??
            toSec(data.clockInAt) ??
            toSec(data.in) ??
            toSec(data.inAt) ??
            null;

          const co =
            toSec(data.clockOut) ??
            toSec(data.clockOutAt) ??
            toSec(data.out) ??
            toSec(data.outAt) ??
            null;
          if (ci == null) return null;
          // include both active and clocked-out shifts so we can correctly dedupe (latest wins)
          return {
            id: d.id,
            name: data.name || data.staffName || data.employeeName || '',
            role: data.role || data.roleName || '',
            store: data.store || data.storeId || '', // id or name (legacy)
            clockInSec: ci,
            clockOutSec: co,
            sourceKey: `cashup_shifts/${d.id}`,
          };
        })
        .filter(Boolean);
    } catch {
      return [];
    }
  }, [orgId]);

  // fetch base list (org-wide), then constrain to *today* and dedupe by person+store (latest)
  const loadActive = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setErr('');
    try {
      const legacy = await readLegacyActive();

      const storeIds = stores.filter((o) => o.value !== 'ALL').map((o) => o.value);
      const nestedLists =
        storeIds.length > 0 ? await Promise.all(storeIds.map((sid) => readNestedPerStore(sid))) : [];

      // merge + dedupe by doc path
      const merged = [];
      const seen = new Set();
      [...legacy, ...nestedLists.flat()].forEach((r) => {
        const key = r.sourceKey || r.id;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(r);
      });

      // A shift remains operationally active until it has an actual clock-out.
      // Do not hide forgotten clock-outs merely because an abandoned-shift threshold elapsed;
      // those are exactly the rows managers need Active Staff to surface.
      const openCandidates = merged.filter((r) => r.clockOutSec == null && (r.clockInSec || 0) > 0);

      // dedupe by person+store (latest clock-in wins)
      const byKey = new Map();
      for (const r of openCandidates) {
        const unifiedStoreId = storeNameToIdRef.current[r.store] || r.store || '';
        const key = `${(r.name || '').toLowerCase()}|${String(unifiedStoreId)}`;
        const prev = byKey.get(key);
        if (!prev || r.clockInSec > prev.clockInSec) byKey.set(key, r);
      }
      const finalList = Array.from(byKey.values())
        .filter((r) => r.clockOutSec == null && (r.clockInSec || 0) > 0)
        .sort((a, b) => b.clockInSec - a.clockInSec);

      setActive(finalList);
    } catch (e) {
      setErr('Failed to load active staff.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, readLegacyActive, readNestedPerStore, stores, abandonedShiftHours]);

  useEffect(() => {
    loadActive();
  }, [loadActive]);

  // live tick for durations
  useEffect(() => {
    const t = setInterval(() => setActive((a) => a.slice()), 30000);
    return () => clearInterval(t);
  }, []);

  // filter by selected store
  const visible = useMemo(() => {
    const selectedId = store.value;
    const base = selectedId === 'ALL' ? active : active.filter((r) => {
      const selectedName = storeIdToNameRef.current[selectedId];
      return r.store === selectedId || r.store === selectedName;
    });
    const allowed = new Set(siteScope.siteIds || []);
    return base.filter((r) => {
      const normalizedStore = storeNameToIdRef.current[r.store] || r.store;
      if (!siteScope.allSites && !allowed.has(normalizedStore)) return false;
      return !archivedStaffNames.has(String(r.name || '').trim().toLowerCase());
    });
  }, [active, store, archivedStaffNames, siteScope.allSites, siteScope.siteIds]);
  const storeLabel = (val) => storeIdToNameRef.current[val] || val || '';

  return (
    <div className="p-6 text-white">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-2xl font-semibold">Active Staff</h1>
        <div className="opacity-75">{visible.length} active</div>
      </div>

      <div className="w-64 mb-4">
        <Select options={stores} value={store} onChange={setStore} styles={selectStyles} theme={selectTheme} />
      </div>

      {err && (
        <div className="mb-3 rounded border border-rose-700/50 bg-rose-900/20 px-3 py-2 text-rose-200">
          {err}
        </div>
      )}

      <div className="rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
        <table className="min-w-[760px] w-full text-sm">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Store</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Clocked In</th>
              <th className="px-3 py-2 text-left">Duration</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-3 py-3 opacity-80" colSpan={5}>Loading…</td>
              </tr>
            )}
            {!loading && visible.length === 0 && (
              <tr>
                <td className="px-3 py-3 opacity-70" colSpan={5}>No staff are currently active.</td>
              </tr>
            )}
            {!loading &&
              visible.map((r) => {
                const ciMs = r.clockInSec * 1000;
                return (
                  <tr key={r.sourceKey || r.id} className="border-t border-white/10">
                    <td className="px-3 py-2">{r.name || '—'}</td>
                    <td className="px-3 py-2">{storeLabel(r.store)}</td>
                    <td className="px-3 py-2">{r.role || ''}</td>
                    <td className="px-3 py-2">
                      {new Date(ciMs).toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-3 py-2">{durHM(ciMs, Date.now())}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex gap-2">
        <button
          onClick={loadActive}
          className="px-3 py-2 rounded text-white"
          style={{ backgroundColor: 'var(--brand-accent)' }}
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
